import * as Immutable from 'immutable';
import { useQuery } from '@tanstack/react-query';

import type { Pagination } from 'stores/PaginationTypes';

import type { PaginatedTeams } from '../actions/TeamsActions';
import TeamsDomain from '../domainActions/TeamsDomain';

const DEFAULT_PAGINATION: Pagination = { page: 1, perPage: 0, query: '' };

type UserFromTeam = { id: string; username: string };
type TeamUsersPerRole = { [roleId: string]: Array<UserFromTeam> };

const getTeamUsersPerRole = (teams: PaginatedTeams): TeamUsersPerRole => {
  let teamUsersPerRole = Immutable.Map<string, Immutable.Set<UserFromTeam>>();

  teams.list.forEach((team) => {
    if (team.users.size === 0 || team.roles.size === 0) {
      return;
    }

    const teamUsers = Immutable.Set<UserFromTeam>(
      team.users.map((userId) => ({ id: userId, username: `${teams.context.users.get(userId).title} (${team.name})` })),
    );

    team.roles.forEach((roleId) => {
      teamUsersPerRole = teamUsersPerRole.set(
        roleId,
        (teamUsersPerRole.get(roleId) ?? Immutable.Set()).union(teamUsers),
      );
    });
  });

  return teamUsersPerRole.toJS();
};

const _loadTeamsAndGetUsers = () =>
  TeamsDomain.loadTeamsPaginated(DEFAULT_PAGINATION).then((paginatedTeams) => getTeamUsersPerRole(paginatedTeams));

const useTeamMembersByRole = () => {
  const { data: users, isInitialLoading: loading } = useQuery({
    queryKey: ['teamMembersByRole'],
    queryFn: _loadTeamsAndGetUsers,
  });

  return loading || !users
    ? {
        isLoading: loading,
        users: {},
      }
    : {
        isLoading: loading,
        users,
      };
};

export default useTeamMembersByRole;
